import { treeNodeUtils, getNodePath } from '~/util';
import { fromJS } from 'immutable';
import undoable, { excludeAction } from 'redux-undo';
import {
	ADD_LESSON,
	ADD_LESSON_ENTITY,
	ADD_SECTION_HEADING_ENTITY,
	REMOVE_SECTION_HEADING_ENTITY,
	ADD_FINAL_QUIZ_ENTITY,
	UPDATE_LESSONS,
	UPDATE_SECTION_HEADINGS,
	SET_ACTIVE_LESSON,
	ADD_TOPIC_ENTITY,
	SET_DRAGGABLE_ENTITY,
	SET_IS_DROP_DISABLED_LESSON,
	ADD_QUIZ_ENTITY,
	UPDATE_QUESTIONS,
	ADD_QUESTION_ENTITY,
	ADD_ANSWER_ENTITY,
	REMOVE_ANSWER_ENTITY,
	UPDATE_WORKSPACE_NODE_TITLE,
	TOGGLE_EXPAND_ALL,
	TOGGLE_EXPAND_ENTITY,
	SORT_FINAL_QUIZZES_OUTLINE,
	REMOVE_ENTITY,
	MOVE_ENTITY,
	INIT_DATA,
	UPDATE_QUESTION_TYPE,
	REMOVE_EXISTING_ENTITY,
	UPDATE_QUESTION_ANSWERS,
} from '~/constants';

/**
 * Initial state of the app
 */
const initialState = {
	disabledDrop: false,
	currentDraggableEntity: null,
	currentDroppableEntity: null,
	activeLesson: null,
	isDropDisabledLesson: null,
	lessons:
		undefined !== LearnDashData.outline &&
		undefined !== LearnDashData.outline.lessons
			? LearnDashData.outline.lessons
			: [],
	quizzes:
		undefined !== LearnDashData.outline &&
		undefined !== LearnDashData.outline.quizzes
			? LearnDashData.outline.quizzes
			: [], // holding our final quizzes
	questions:
		undefined !== LearnDashData.outline &&
		undefined !== LearnDashData.outline.questions
			? LearnDashData.outline.questions
			: [],
	sections:
		undefined !== LearnDashData.outline &&
		undefined !== LearnDashData.outline.sections
			? LearnDashData.outline.sections
			: [],
	expandAll: false,
	expandedItems: [],
	refresh: null, // which data we need to refresh
};

// Check if we have existing lessons/questions, set the first entity as the active one.
if (
	LearnDashData.post_data &&
	'sfwd-quiz' !== LearnDashData.post_data.builder_post_type
) {
	if (
		LearnDashData.outline &&
		LearnDashData.outline.lessons &&
		LearnDashData.outline.lessons.length
	) {
		// get index of first lesson (skip section heading)
		const firstLesson = LearnDashData.outline.lessons.findIndex(
			( node ) => 'sfwd-lessons' === node.type
		);
		if ( LearnDashData.outline.lessons[ firstLesson ] ) {
			initialState.activeLesson =
				LearnDashData.outline.lessons[ firstLesson ].ID;
		}
	}
} else if (
	LearnDashData.outline &&
	LearnDashData.outline.questions &&
	LearnDashData.outline.questions.length
) {
	initialState.activeLesson = LearnDashData.outline.questions[ 0 ].ID;
	LearnDashData.outline.questions[ 0 ].expanded = true;
}

/**
 * Workspace related actions here.
 *
 * @param {*} state
 * @param {*} action
 */
const workspaceReducer = ( state = initialState, action ) => {
	const immutableLessons = fromJS( state.lessons );
	// Final quizzes, which are separate from lessons (which can contain topics and quizzes)
	const immutableQuizzes = fromJS( state.quizzes );
	// Questions
	const immutableQuestions = fromJS( state.questions );
	// Sections
	const immutableSections = state.sections
		? fromJS( state.sections )
		: fromJS( [] );

	switch ( action.type ) {
		/**
		 * Update the lessons field in the store.
		 */
		case UPDATE_LESSONS: {
			return {
				...state,
				lessons: action.payload,
			};
		}

		/**
		 * Update the sections field in the store.
		 */
		case UPDATE_SECTION_HEADINGS: {
			return {
				...state,
				sections: action.payload,
			};
		}

		/**
		 * Adds a new lesson within the workspace at a specific position
		 */
		case ADD_LESSON_ENTITY: {
			let index = action.payload.index;

			// Check if we have a valid index.
			if ( undefined === index || isNaN( index ) ) {
				index = state.lessons.length;
			}

			// Use the insert method to add the new object.
			const lessons = immutableLessons
				.insert( index, {
					ID: action.payload.ID,
					post_title: action.payload.post_title,
					url: action.payload.url,
					edit_link: action.payload.edit_link,
					tree: [],
					expanded: false,
					type: 'sfwd-lessons',
				} )
				.toJS();

			// Set the active lesson which is the newly added one.
			const activeLesson = action.payload.ID;

			// which data we need to refresh
			const refresh = 'lesson';

			return {
				...state,
				lessons,
				activeLesson,
				refresh,
			};
		}

		/**
		 * Adds a new section heading within the workspace at a specific position
		 * Adds to workspace lesson but also to workspace sections
		 */
		case ADD_SECTION_HEADING_ENTITY: {
			let index = action.payload.index;
			let lessonIndex = 0;

			// Check if we have a valid index.
			if ( undefined === index || isNaN( index ) ) {
				index = state.lessons.length;
			}

			if ( state.activeLesson ) {
				lessonIndex = immutableLessons.findIndex(
					( node ) => node.get( 'ID' ) === state.activeLesson
				);
			} else {
				lessonIndex = index;
			}

			// Use the insert method to add the new object.
			const lessons = immutableLessons
				.insert( parseInt( lessonIndex + 1, 10 ), {
					order: action.payload.order,
					ID: action.payload.ID,
					post_title: action.payload.post_title,
					url: action.payload.url,
					edit_link: action.payload.edit_link,
					tree: [],
					expanded: false,
					type: 'section-heading',
				} )
				.toJS();

			const sections = immutableSections
				.insert( index, {
					order: action.payload.order,
					ID: action.payload.ID,
					post_title: action.payload.post_title,
					url: action.payload.url,
					edit_link: action.payload.edit_link,
					tree: [],
					expanded: false,
					type: 'section-heading',
				} )
				.toJS();

			// which data we need to refresh
			const refresh = 'lesson';

			return {
				...state,
				lessons,
				sections,
				refresh,
			};
		}

		/**
		 * Sets the active lesson in the workspace.
		 */
		case SET_ACTIVE_LESSON: {
			return {
				...state,
				activeLesson: parseInt( action.payload.activeLesson ),
			};
		}

		/**
		 * Adds a new topic to a lesson
		 */
		case ADD_TOPIC_ENTITY: {
			let topicDestinationIndex = 0;
			let lessonIndex = null;
			let activeLesson = state.activeLesson;

			// We are passing a parent to which the topic will be added to.
			if ( action.parent ) {
				// get parent node
				lessonIndex = immutableLessons.findIndex(
					( node ) => node.get( 'ID' ) === action.parent
				);

				// If we found the parent node.
				if ( -1 !== lessonIndex ) {
					// Get the tree
					const parentNode = immutableLessons.get( lessonIndex );
					const parentNodeTree = parentNode.get( 'tree' );
					topicDestinationIndex = action.destinationIndex;
					activeLesson = action.parent;

					if (
						0 < topicDestinationIndex ||
						null === topicDestinationIndex
					) {
						// if we are trying to drop before a quiz, find the first quiz index and insert topic at that index
						const firstQuizIndex = parentNodeTree.findIndex(
							( el ) => {
								return (
									'quiz' === el.get( 'type' ) ||
									'sfwd-quiz' === el.get( 'type' )
								);
							}
						);

						if ( -1 !== firstQuizIndex ) {
							topicDestinationIndex = firstQuizIndex;
						} else {
							topicDestinationIndex = parentNodeTree.size;
						}
					}
				}
			} else if ( state.activeLesson ) {
				// Get the active lesson
				lessonIndex = immutableLessons.findIndex(
					( node ) => node.get( 'ID' ) === state.activeLesson
				);
				if ( -1 !== lessonIndex ) {
					const activeLessonNode = immutableLessons.get(
						lessonIndex
					);
					const activeLessonTree = activeLessonNode.get( 'tree' );
					topicDestinationIndex = activeLessonTree.size;

					if ( undefined === action.payload.destinationIndex ) {
						// if we are trying to drop before a quiz, find the first quiz index and insert topic at that index
						const firstQuizIndex = activeLessonTree.findIndex(
							( el ) => {
								return (
									'quiz' === el.get( 'type' ) ||
									'sfwd-quiz' === el.get( 'type' )
								);
							}
						);

						if ( -1 !== firstQuizIndex ) {
							topicDestinationIndex = firstQuizIndex;
						}
					}
				}
			}

			// Add extra information.
			action.payload.type = 'sfwd-topic';
			action.payload.tree = [];
			action.payload.expanded = false;

			// Make the update
			const lessons = immutableLessons
				.updateIn( [ lessonIndex, 'tree' ], ( tree ) => {
					return tree.insert( topicDestinationIndex, action.payload );
				} )
				.toJS();

			// which data we need to refresh
			const refresh = 'topic';

			// Return with updated values if any.
			return {
				...state,
				lessons,
				activeLesson,
				refresh,
			};
		}

		/**
		 * Adds a new quiz entity to the workspace
		 */
		case ADD_QUIZ_ENTITY: {
			// Default values
			let nodePath = -1;
			let activeLesson = state.activeLesson;

			// add some extra data.
			action.payload.type = 'sfwd-quiz';
			action.payload.expanded = false;

			if ( action.parent ) {
				nodePath = getNodePath( state.lessons, action.parent, true );
				activeLesson = action.parent;
			} else if ( state.activeLesson ) {
				// Adds quiz to the current active lesson. Get the active lesson first.
				nodePath = getNodePath(
					state.lessons,
					state.activeLesson,
					true
				);
			}

			// If we have a path
			if ( -1 !== nodePath ) {
				// Make the update.
				const lessons = immutableLessons
					.updateIn( nodePath, ( tree ) => {
						return tree.push( action.payload );
					} )
					.toJS();

				// which data we need to refresh
				const refresh = 'quiz';

				return {
					...state,
					lessons,
					activeLesson,
					refresh,
				};
			}
			// there is no active lesson, add to final quizzes
			const index = state.quizzes.length;

			// Use the insert method to add the new object.
			const quizzes = immutableQuizzes
				.insert( index, {
					ID: action.payload.ID,
					post_title: action.payload.post_title,
					edit_link: action.payload.edit_link,
					url: action.payload.url,
					tree: [],
					expanded: true,
					type: 'sfwd-quiz',
				} )
				.toJS();

			return {
				...state,
				quizzes,
			};
		}

		/**
		 * Update the questions field in the store.
		 */
		case UPDATE_QUESTIONS: {
			return {
				...state,
				questions: action.payload,
			};
		}

		/**
		 * Adds a new question within the workspace at a specific position
		 */
		case ADD_QUESTION_ENTITY: {
			let index = action.payload.index;

			// Check if we have a valid index.
			if ( undefined === index || isNaN( index ) ) {
				index = state.questions.length;
			}

			// answers objects when none are provided.
			// this need to match backend defaults
			const newAnswerObject = {
				answer: '',
				correct: false,
				graded: '1',
				gradedType: 'text',
				gradingProgression: '',
				html: false,
				points: 1,
				sortString: '',
				sortStringHtml: false,
				type: 'answer',
			};
			const newAnswersObject = {
				assessment_answer: [ newAnswerObject ],
				classic_answer: [ newAnswerObject ],
				cloze_answer: [ newAnswerObject ],
				essay: [ newAnswerObject ],
				free_answer: [ newAnswerObject ],
				matrix_sort_answer: [ newAnswerObject ],
				sort_answer: [ newAnswerObject ],
			};

			// Use the insert method to add the new object.
			const questions = immutableQuestions
				.insert( index, {
					ID: action.payload.ID,
					post_title: action.payload.post_title,
					url: action.payload.url,
					edit_link: action.payload.edit_link,
					answers: action.payload.answers
						? action.payload.answers
						: newAnswersObject,
					question_type: action.payload.question_type
						? action.payload.question_type
						: 'single',
					post_content: action.payload.post_content
						? action.payload.post_content
						: '',
					points: action.payload.points ? action.payload.points : 1,
					expanded: false,
					type: 'sfwd-question',
				} )
				.toJS();

			// Set the active lesson which is the newly added one.
			const activeLesson = action.payload.ID;

			// which data we need to refresh
			const refresh = 'question';

			return {
				...state,
				questions,
				activeLesson,
				refresh,
			};
		}

		/**
		 * Adds a new answer within the workspace
		 */
		case ADD_ANSWER_ENTITY: {
			let questions = state.questions;
			const answerType =
				LearnDashData.questions_types_map[
					action.parent.question_type
				];
			const parentIndex = immutableQuestions.findIndex(
				( node ) => node.get( 'ID' ) === action.parent.ID
			);
			const index =
				state.questions[ parentIndex ].answers[ answerType ].length + 1;
			const nodePath = [
				parentIndex,
				'answers',
				answerType,
				action.payload.index,
			];

			if ( nodePath.length ) {
				// we need to update a tree
				const treePath = nodePath.slice( 0, nodePath.length - 1 );

				questions = immutableQuestions
					.updateIn( treePath, ( tree ) => {
						return tree.insert( index, action.payload );
					} )
					.toJS();

				return {
					...state,
					questions,
				};
			}

			return state;
		}

		/**
		 * Removes an answer within the workspace
		 */
		case REMOVE_ANSWER_ENTITY: {
			let questions = state.questions;
			const answerType =
				LearnDashData.questions_types_map[
					action.parent.question_type
				];
			const parentIndex = immutableQuestions.findIndex(
				( node ) => node.get( 'ID' ) === action.parent.ID
			);
			const nodePath = [
				parentIndex,
				'answers',
				answerType,
				action.payload,
			];

			if ( nodePath.length ) {
				// we need to update a tree
				const treePath = nodePath.slice( 0, nodePath.length - 1 );

				questions = immutableQuestions
					.updateIn( treePath, ( tree ) => {
						return tree.delete( action.payload ).toJS();
					} )
					.toJS();

				return {
					...state,
					questions,
				};
			}

			return state;
		}

		/**
		 * Updates a node title.
		 */
		case UPDATE_WORKSPACE_NODE_TITLE: {
			if ( action.payload.ID && action.payload.post_title ) {
				let type = '';
				const entities = {
					lessons: state.lessons,
					questions: state.questions,
				};
				if (
					'sfwd-quiz' === LearnDashData.post_data.builder_post_type
				) {
					type = 'questions';
				} else {
					type = 'lessons';
				}
				const entityObject = Object.values( entities[ type ] );
				const node = treeNodeUtils.getNodeByKey(
					entityObject,
					action.payload.ID
				);

				if ( node ) {
					node.post_title = action.payload.post_title;

					return {
						...state,
						[ type ]: entities[ type ],
					};
				}

				// try to look into the quizzes
				const quizzes = Object.values( state.quizzes );
				const quizNode = treeNodeUtils.getNodeByKey(
					quizzes,
					action.payload.ID
				);

				if ( quizNode ) {
					quizNode.post_title = action.payload.post_title;

					return {
						...state,
						quizzes,
					};
				}

				return state;
			}

			return state;
		}

		/**
		 * Adds a quiz to the final quizzes section.
		 */
		case ADD_FINAL_QUIZ_ENTITY: {
			let index = action.index;

			// Check if we have a valid index.
			if ( undefined === index || isNaN( index ) ) {
				index = state.quizzes.length;
			}

			// Use the insert method to add the new object.
			const quizzes = immutableQuizzes
				.insert( index, {
					ID: action.payload.ID,
					post_title: action.payload.post_title,
					edit_link: action.payload.edit_link,
					url: action.payload.url,
					tree: [],
					expanded: true,
					type: 'sfwd-quiz',
				} )
				.toJS();

			return {
				...state,
				quizzes,
			};
		}

		/**
		 * Used when sorting quizzes.
		 */
		case SORT_FINAL_QUIZZES_OUTLINE: {
			return {
				...state,
				quizzes: action.payload,
			};
		}

		/**
		 * Remove any entity from the workspace based on the entity ID
		 */
		case REMOVE_EXISTING_ENTITY:
		case REMOVE_SECTION_HEADING_ENTITY:
		case REMOVE_ENTITY: {
			if ( action.payload.ID ) {
				// Default value for our lessons and quizzes, which is the same state
				let quizzes = state.quizzes;
				let type = '';
				const entities = {
					lessons: state.lessons,
					questions: state.questions,
				};
				let immutableEntities = null;
				if (
					'sfwd-quiz' === LearnDashData.post_data.builder_post_type
				) {
					type = 'questions';
					immutableEntities = immutableQuestions;
				} else {
					type = 'lessons';
					immutableEntities = immutableLessons;
				}
				// Whether we found the node.
				let found = false;

				// Look if we need to remove a lesson.
				const nodePath = getNodePath(
					entities[ type ],
					action.payload.ID
				);

				if ( -1 !== nodePath ) {
					entities[ type ] = immutableEntities
						.deleteIn( nodePath )
						.toJS();
					found = true;

					const activeLesson =
						0 < entities[ type ].length
							? entities[ type ][ 0 ].ID
							: null;

					// Found, return updated entities.
					return {
						...state,
						[ type ]: entities[ type ],
						activeLesson,
					};
				}

				if (
					'sfwd-quiz' !== LearnDashData.post_data.builder_post_type
				) {
					// if node has not been found in lessons, look into final quizzes
					if ( ! found ) {
						const quizNodePath = getNodePath(
							state.quizzes,
							action.payload.ID
						);

						if ( -1 !== quizNodePath ) {
							quizzes = immutableQuizzes
								.deleteIn( quizNodePath )
								.toJS();
						}

						return {
							...state,
							quizzes,
						};
					}
				}

				// Return everything now.
				return {
					...state,
					[ type ]: entities[ type ],
					quizzes,
				};
			}

			return state;
		}

		/**
		 * Moves an entity up or down
		 */
		case MOVE_ENTITY: {
			if ( ! action.payload ) {
				return state;
			}

			let type = '';
			let nodePath = [];
			const entities = {
				questions: state.questions,
				lessons: state.lessons,
			};
			let immutableEntities = null;
			if ( 'sfwd-quiz' === LearnDashData.post_data.builder_post_type ) {
				type = 'questions';
				immutableEntities = immutableQuestions;
			} else {
				type = 'lessons';
				immutableEntities = immutableLessons;
			}

			if ( 'answer' === action.payload.type ) {
				const parentIndex = immutableEntities.findIndex(
					( node ) => node.get( 'ID' ) === action.payload.parentID
				);
				nodePath = [
					parentIndex,
					'answers',
					action.payload.answerType,
					action.payload.index,
				];
			} else {
				nodePath = getNodePath(
					entities[ type ],
					action.payload.ID,
					false
				);
			}

			if ( nodePath.length ) {
				// Get the index of the node which is the last element in the array.
				const currentIndex = nodePath[ nodePath.length - 1 ];
				const currentNode = immutableEntities.getIn( nodePath );

				// set the new index depending on the direction.
				let newIndex = currentIndex + 1;
				if ( 'up' === action.direction ) {
					newIndex = currentIndex - 1;
				}

				// We are at the top level
				if ( 1 === nodePath.length ) {
					entities[ type ] = immutableEntities
						.deleteIn( nodePath )
						.insert( newIndex, currentNode )
						.toJS();
				} else {
					// we need to update a tree
					const treePath = nodePath.slice( 0, nodePath.length - 1 );

					entities[ type ] = immutableEntities
						.updateIn( treePath, ( tree ) => {
							return tree
								.delete( currentIndex )
								.insert( newIndex, currentNode );
						} )
						.toJS();
				}

				return {
					...state,
					[ type ]: entities[ type ],
				};
			}

			return state;
		}

		/**
		 * Sets the draggable and droppable entities.
		 */
		case SET_DRAGGABLE_ENTITY: {
			return {
				...state,
				currentDraggableEntity: action.payload.currentDraggableEntity,
				currentDroppableEntity: action.payload.currentDroppableEntity,
			};
		}

		/**
		 * Sets the is drop disabled value
		 */
		case SET_IS_DROP_DISABLED_LESSON: {
			return {
				...state,
				isDropDisabledLesson: action.payload.isDropDisabledLesson,
			};
		}

		/**
		 * Used for the global expand/collapse all
		 * Only affects top level items (lessons in course builder or questions in quiz builder)
		 */
		case TOGGLE_EXPAND_ALL: {
			let type = '';
			const entities = {
				questions: state.questions,
				lessons: state.lessons,
			};

			if ( 'sfwd-quiz' === LearnDashData.post_data.builder_post_type ) {
				type = 'questions';
			} else {
				type = 'lessons';
			}

			// Get all the top level items (ignore section headings)
			const realBuilderItems = entities[ type ].filter(
				( node ) =>
					'sfwd-lessons' === node.type ||
					'sfwd-question' === node.type
			);

			// Everything was already expanded, so collapse all
			if ( state.expandAll ) {
				return {
					...state,
					expandedItems: [],
					expandAll: false,
				};
			}
			// Everything was collapsed, so expand all
			// Add all top level items IDs in expandedItems
			return {
				...state,
				expandedItems: realBuilderItems.map( ( item ) => item.ID ),
				expandAll: true,
			};
		}

		/**
		 * Used for the entity expand/collapse
		 */
		case TOGGLE_EXPAND_ENTITY: {
			if ( action.payload.ID ) {
				let type = '';
				let entityObject = null;
				const entities = {
					questions: state.questions,
					lessons: state.lessons,
				};

				if (
					'sfwd-quiz' === LearnDashData.post_data.builder_post_type
				) {
					type = 'questions';
				} else {
					type = 'lessons';
				}

				// Get all the top level items (ignore section headings)
				const realBuilderItems = entities[ type ].filter(
					( node ) =>
						'sfwd-lessons' === node.type ||
						'sfwd-question' === node.type
				);

				// Expand/Collapse lessons or questions
				if (
					'sfwd-lessons' === action.payload.type ||
					'sfwd-question' === action.payload.type
				) {
					const isExpanded =
						-1 !== state.expandedItems.indexOf( action.payload.ID );

					// If the item is not expanded, add it to expandedItems
					// If the item is already expanded, remove it
					const newExpandedItems = ! isExpanded
						? [ ...state.expandedItems, action.payload.ID ]
						: state.expandedItems.filter(
								( item ) => item !== action.payload.ID
						  );

					return {
						...state,
						expandedItems: newExpandedItems,
						expandAll:
							realBuilderItems.length === newExpandedItems.length,
					};
				} else if ( 'sfwd-topic' === action.payload.type ) {
					// Expand/Collapse topics

					entityObject = Object.values(
						entities[ type ][ action.parent ].tree
					);
					const node = treeNodeUtils.getNodeByKey(
						entityObject,
						action.payload.ID
					);

					if ( node ) {
						node.expanded = ! action.payload.expanded;

						return {
							...state,
							[ type ]: entities[ type ],
						};
					}
				}

				return state;
			}
			return state;
		}

		/**
		 * Updates a question type.
		 */
		case UPDATE_QUESTION_TYPE: {
			if ( action.payload.ID ) {
				const nodeIndex = immutableQuestions.findIndex(
					( node ) => node.get( 'ID' ) === action.payload.ID
				);

				if ( -1 !== nodeIndex ) {
					const questions = immutableQuestions
						.update( nodeIndex, ( node ) =>
							node
								.set(
									'question_type',
									action.payload.question_type
								)
								.set( 'points', action.payload.points )
								.set(
									'post_content',
									action.payload.post_content
								)
						)
						.toJS();

					return {
						...state,
						questions,
					};
				}

				return state;
			}

			return state;
		}

		/**
		 * Updates the answers for a question. Used mainly when selecting the correct answer to refresh everything.
		 */
		case UPDATE_QUESTION_ANSWERS: {
			if ( action.question.ID ) {
				const nodeIndex = immutableQuestions.findIndex(
					( node ) => node.get( 'ID' ) === action.question.ID
				);

				if ( -1 !== nodeIndex ) {
					const questions = immutableQuestions
						.update( nodeIndex, ( node ) =>
							node.setIn(
								[
									'answers',
									LearnDashData.questions_types_map[
										action.question.question_type
									],
								],
								action.answers
							)
						)
						.toJS();

					return {
						...state,
						questions,
					};
				}
			}

			return state;
		}

		default: {
			return state;
		}
	}
};

export const workspace = undoable( workspaceReducer, {
	filter: excludeAction( [
		INIT_DATA,
		SET_DRAGGABLE_ENTITY,
		SET_ACTIVE_LESSON,
		SET_IS_DROP_DISABLED_LESSON,
		ADD_LESSON,
		TOGGLE_EXPAND_ALL,
		TOGGLE_EXPAND_ENTITY,
		REMOVE_EXISTING_ENTITY, // used when moving things.
		UPDATE_QUESTION_TYPE,
		ADD_SECTION_HEADING_ENTITY,
		REMOVE_SECTION_HEADING_ENTITY,
		UPDATE_SECTION_HEADINGS,
	] ),
} );
